import isObject from 'lodash/isObject';
import isUndefined from 'lodash/isUndefined';
import EnvironmentUtils from '@spothero/utils/environment';
import FacilityAPI from 'api/facility';
import ReservationAPI from 'api/reservation';
import StripeUtils from 'utils/stripe';
import {userUpdate} from 'user/user-actions';

export const CHECKOUT_FETCH_RATES = 'CHECKOUT_FETCH_RATES';
export const CHECKOUT_SHOW_CHANGE_DATE_TIME = 'CHECKOUT_SHOW_CHANGE_DATE_TIME';
export const CHECKOUT_HIDE_CHANGE_DATE_TIME = 'CHECKOUT_HIDE_CHANGE_DATE_TIME';
export const CHECKOUT_HIDE_RECOMMENDED_SPOTS_DETAILS = 'CHECKOUT_HIDE_RECOMMENDED_SPOTS_DETAILS';
export const CHECKOUT_UPDATE_DATA = 'CHECKOUT_UPDATE_DATA';
export const CHECKOUT_USER_CHANGE = 'CHECKOUT_USER_CHANGE';
export const CHECKOUT_TOGGLE_TIME_EXTENDED_DETAILS = 'CHECKOUT_TOGGLE_TIME_EXTENDED_DETAILS';
export const CHECKOUT_HIDE_REDUNDANT_MONTHLY_DETAILS = 'CHECKOUT_HIDE_REDUNDANT_MONTHLY_DETAILS';
export const CHECKOUT_PAYMENT_TOKEN_REQUESTED = 'CHECKOUT_PAYMENT_TOKEN_REQUESTED';
export const CHECKOUT_PAYMENT_ERROR = 'CHECKOUT_PAYMENT_ERROR';
export const CHECKOUT_ON_PURCHASE = 'CHECKOUT_ON_PURCHASE';
export const CHECKOUT_PURCHASE_ERROR = 'CHECKOUT_PURCHASE_ERROR';

const isMobileDevice = EnvironmentUtils.isMobileDevice();

const purchaseError = (dispatch, errors, extra) => {
    let newState;

    if (errors[0].code === 'previously_purchased_monthly') {
        const {
            created,
            parkingPassURL
        } = JSON.parse(errors[0].messages[0]);

        newState = {
            redundantMonthlyReservationModalDate: created,
            redundantMonthlyReservationModalParkingPassURL: parkingPassURL,
            showRedundantMonthlyReservationModal: true
        };
    } else {
        const messages = [];

        errors.forEach(error => {
            if (isObject(error.messages[0])) {
                if (!isUndefined(error.messages[0].all_errors)) {
                    messages.push(error.messages[0].all_errors[0]);
                }
            } else {
                messages.push(error.messages[0]);
            }
        });

        if (errors[0].code === 'invalid_referral' && extra && !isUndefined(extra.spothero_credit)) {
            dispatch(userUpdate({
                credit: extra.spothero_credit
            }));
        }

        newState = {
            checkoutError: {
                subtitle: `Correct the following mistakes then press Pay & Reserve again. Don't worry, you won't be charged twice.`,
                errors: messages
            }
        };
    }

    return dispatch({
        type: CHECKOUT_PURCHASE_ERROR,
        payload: newState
    });
};

export const fetchRates = ({spotId, starts, ends, eventId, airport, includeFacility, meta}) => {
    return (dispatch, getState) => {
        return dispatch({
            type: CHECKOUT_FETCH_RATES,
            payload: FacilityAPI.getRates({
                spotId,
                starts,
                ends,
                eventId,
                airport,
                includeFacility
            }),
            meta: {
                ...meta,
                starts,
                ends,
                timezone: getState().city.data.timezone
            }
        });
    };
};

export const showChangeDateTime = ({
    title = 'Change Time',
    message = 'Just FYI, the price or availability might change if you choose different options here.',
    isStaleStart = false
} = {}) => {
    return {
        type: CHECKOUT_SHOW_CHANGE_DATE_TIME,
        payload: {
            title,
            message,
            isStaleStart
        }
    };
};

export const hideChangeDateTime = (showRecommendedSpots = false) => {
    return {
        type: CHECKOUT_HIDE_CHANGE_DATE_TIME,
        payload: {
            showRecommendedSpots
        }
    };
};

export const hideRecommendedSpots = (showChangeDateTimeModal = false) => {
    return {
        type: CHECKOUT_HIDE_RECOMMENDED_SPOTS_DETAILS,
        payload: {
            showChangeDateTimeModal
        }
    };
};

export const updateData = data => {
    return {
        type: CHECKOUT_UPDATE_DATA,
        payload: data
    };
};

export const userChange = ({user, userNormalized, isMonthly, extraReservationItems}) => {
    return {
        type: CHECKOUT_USER_CHANGE,
        payload: {
            user,
            userNormalized,
            isMonthly,
            extraReservationItems
        }
    };
};

export const toggleTimeExtendedDetails = () => {
    return {
        type: CHECKOUT_TOGGLE_TIME_EXTENDED_DETAILS
    };
};

export const hideRedundantMonthlyDetails = () => {
    return {
        type: CHECKOUT_HIDE_REDUNDANT_MONTHLY_DETAILS
    };
};

export const purchase = ({reservationData, isAdmin}) => {
    return (dispatch, getState) => {
        const {
            selectedCreditCard,
            newCardInfo
        } = reservationData;
        const {
            user: {
                data: userData
            },
            searchRequest: {
                monthly: isMonthly,
                rebook_reservation_id: rebookReservationId,
                starts: monthlyStarts,
                eid: eventId
            },
            spot: {
                selected: {
                    facility,
                    selectedRate
                }
            },
            checkout
        } = getState();
        const checkoutData = {
            user: userData,
            isMonthly,
            monthlyStarts,
            eventId,
            ...checkout,
            ...reservationData
        };

        if (checkout.isRebookingReservation && rebookReservationId) {
            return dispatch({
                type: CHECKOUT_ON_PURCHASE,
                payload: ReservationAPI.rebook({
                    checkoutData,
                    selectedRate,
                    facility,
                    rebookReservationId,
                    onError: (errors, extra) => {
                        purchaseError(dispatch, errors, extra);
                    }
                })
            });
        } else if (selectedCreditCard === 'new') {
            dispatch({
                type: CHECKOUT_PAYMENT_TOKEN_REQUESTED
            });

            StripeUtils.createToken({
                isAdmin,
                onSuccess(tokenId) {
                    return dispatch({
                        type: CHECKOUT_ON_PURCHASE,
                        payload: ReservationAPI.add({
                            checkoutData,
                            selectedRate,
                            monthlyStarts,
                            facility,
                            checkoutStartTime: checkout.checkoutStartTime,
                            stripeToken: tokenId,
                            source: (isMobileDevice) ? 'mobile-web' : 'web',
                            setNewCardAsDefault: newCardInfo.isDefault,
                            onError: (errors, extra) => {
                                purchaseError(dispatch, errors, extra);
                            }
                        })
                    });
                },
                onError(errorMessage) {
                    return dispatch({
                        type: CHECKOUT_PAYMENT_ERROR,
                        payload: {
                            errors: [errorMessage]
                        }
                    });
                }
            });
        } else {
            return dispatch({
                type: CHECKOUT_ON_PURCHASE,
                payload: ReservationAPI.add({
                    checkoutData,
                    selectedRate,
                    monthlyStarts,
                    facility,
                    checkoutStartTime: checkout.checkoutStartTime,
                    source: (isMobileDevice) ? 'mobile-web' : 'web',
                    onError: (errors, extra) => {
                        purchaseError(dispatch, errors, extra);
                    }
                })
            });
        }
    };
};
